import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import SearchRounded from '@mui/icons-material/SearchRounded';
import type { SelectChangeEvent } from '@mui/material';
import {
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { useCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import type { InsightFilter } from 'src/components/dashboard/DashboardPage/widgets/InsightWheelWidget/index';
import type { SentimentFilter } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import { styles } from './index.styles';

export const INSIGHT_WHEEL_FILTER_GROUP_TEST_ID = 'insight-wheel-filter_group';
export const INSIGHT_WHEEL_DROPDOWN_TEST_ID = 'insight-wheel-dropdown';
export const INSIGHT_WHEEL_TEXT_FILTER_SEARCH_ICON_TEST_ID =
  'insight-wheel-text-filter-search';
export const INSIGHT_WHEEL_TEXT_FILTER_LOADING_ICON_TEST_ID =
  'insight-wheel-text-filter-loading';

export interface InsightWheelFilterGroupProps {
  setInsightWheelFilter: Dispatch<SetStateAction<InsightFilter>>;
  insightWheelFilter: InsightFilter;
}

const InsightWheelFilterGroup: FC<InsightWheelFilterGroupProps> = ({
  setInsightWheelFilter,
  insightWheelFilter,
}) => {
  const { t } = useTranslation('dashboard');
  const { textFilter, setTextFilter, textFilterLoading } = useCodingResults();
  const handleTextFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTextFilter(event.target.value);
  };

  const menuProps = {
    PaperProps: {
      sx: styles.menuProps,
    },
  };

  const insightWheelDropdownData: {
    label: string;
    value: SentimentFilter;
  }[] = [
    { label: t('InsightWheel.allSentiments'), value: 'totalOfAllCounts' },
    { label: t('InsightWheel.positiveTopics'), value: 'positiveCount' },
    { label: t('InsightWheel.neutralTopics'), value: 'neutralCount' },
    { label: t('InsightWheel.negativeTopics'), value: 'negativeCount' },
  ];

  return (
    <Stack sx={styles.stack} data-test-id={INSIGHT_WHEEL_FILTER_GROUP_TEST_ID}>
      <TextField
        sx={styles.insightWheelSearch}
        placeholder={t('common:search')}
        onChange={handleTextFilterChange}
        value={textFilter}
        InputProps={{
          startAdornment: textFilterLoading ? (
            <CircularProgress
              size="1.5rem"
              data-test-id={INSIGHT_WHEEL_TEXT_FILTER_LOADING_ICON_TEST_ID}
            />
          ) : (
            <SearchRounded
              data-test-id={INSIGHT_WHEEL_TEXT_FILTER_SEARCH_ICON_TEST_ID}
            />
          ),
        }}
      />
      <Select
        value={insightWheelFilter.sentimentFilter}
        data-test-id={INSIGHT_WHEEL_DROPDOWN_TEST_ID}
        sx={styles.insightWheelDropdown}
        onChange={(event: SelectChangeEvent) => {
          setInsightWheelFilter((prev) => ({
            ...prev,
            sentimentFilter: event.target.value as SentimentFilter,
          }));
        }}
        MenuProps={menuProps}
      >
        {insightWheelDropdownData.map(({ label, value }, i) => (
          <MenuItem
            key={i}
            value={value}
            sx={styles.insightWheelDropdownItem}
            data-test-id={`${INSIGHT_WHEEL_DROPDOWN_TEST_ID}-${value}`}
          >
            {label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};

export default InsightWheelFilterGroup;
